// frontend/src/components/SystemErrorsPanel.tsx
// Раздел «🚨 Ошибки системы» в админ-панели
// Показывает все системные ошибки с фильтрами и действиями

import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface SystemErrorItem {
  _id: string;
  level: 'critical' | 'error' | 'warning' | 'info';
  source: string;
  message: string;
  stack?: string;
  context?: any;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: { name?: string; email?: string };
  resolvedNote?: string;
  createdAt: string;
  updatedAt: string;
}

interface Counts {
  total: number;
  byLevel: { critical: number; error: number; warning: number; info: number };
}

const LEVEL_META: Record<string, { icon: string; label: string; color: string; bg: string }> = {
  critical: { icon: '🆘', label: 'Критично', color: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)' },
  error:    { icon: '🚨', label: 'Ошибка',   color: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)' },
  warning:  { icon: '⚠️', label: 'Предупр.', color: '#d97706', bg: 'rgba(217, 119, 6, 0.1)' },
  info:     { icon: 'ℹ️', label: 'Инфо',     color: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)' },
};

const SystemErrorsPanel: React.FC = () => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [items, setItems] = useState<SystemErrorItem[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Фильтры
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [filterResolved, setFilterResolved] = useState<'false' | 'true' | ''>('false');
  const [filterSource, setFilterSource] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Раскрытые карточки
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchCounts = async () => {
    try {
      const res = await api.get('/system-errors/count');
      setCounts(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchItems = async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: p, limit: 30 };
      if (filterLevel) params.level = filterLevel;
      if (filterResolved !== '') params.resolved = filterResolved;
      if (filterSource.trim()) params.source = filterSource.trim();
      if (search.trim()) params.search = search.trim();

      const res = await api.get('/system-errors', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Ошибка загрузки');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCounts(); }, []);

  useEffect(() => {
    setPage(1);
    fetchItems(1);
  }, [filterLevel, filterResolved, filterSource]);

  // Polling счётчика каждые 30 секунд
  useEffect(() => {
    const t = setInterval(fetchCounts, 30000);
    return () => clearInterval(t);
  }, []);

  const handleSearch = () => { setPage(1); fetchItems(1); };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleResolve = async (id: string) => {
    if (!window.confirm('Пометить ошибку решённой?')) return;
    try {
      await api.put(`/system-errors/${id}/resolve`, {});
      fetchItems(page);
      fetchCounts();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось');
    }
  };

  const handleResolveAll = async () => {
    if (counts?.total === 0) return;
    if (!window.confirm(`Пометить ВСЕ ${counts?.total} нерешённых ошибок решёнными?`)) return;
    try {
      const res = await api.put('/system-errors/resolve-all', {});
      alert(`✅ ${res.data.message}`);
      fetchItems(page);
      fetchCounts();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('УДАЛИТЬ запись об ошибке безвозвратно?')) return;
    try {
      await api.delete(`/system-errors/${id}`);
      fetchItems(page);
      fetchCounts();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось');
    }
  };

  const fmtDate = (d?: string) => d
    ? new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <div>
      {/* HEADER */}
      <div style={styles.header}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          🚨 Ошибки системы
          {counts && counts.total > 0 && (
            <span style={styles.totalBadge}>{counts.total}</span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { fetchItems(); fetchCounts(); }} style={styles.refreshBtn} disabled={loading}>
            🔄 Обновить
          </button>
          {counts && counts.total > 0 && (
            <button onClick={handleResolveAll} style={styles.resolveAllBtn}>
              ✅ Пометить все решёнными
            </button>
          )}
        </div>
      </div>

      {/* СТАТИСТИКА ПО УРОВНЯМ */}
      {counts && (
        <div style={styles.statsRow}>
          {(['critical', 'error', 'warning', 'info'] as const).map(level => {
            const meta = LEVEL_META[level];
            const val = counts.byLevel[level] || 0;
            return (
              <div
                key={level}
                style={{ ...styles.statCard, borderColor: val > 0 ? meta.color : '#e5e7eb', background: val > 0 ? meta.bg : '#fff' }}
                onClick={() => setFilterLevel(filterLevel === level ? '' : level)}
                title={`Нажми чтобы фильтровать по «${meta.label}»`}
              >
                <div style={{ fontSize: 24 }}>{meta.icon}</div>
                <div style={{ ...styles.statValue, color: val > 0 ? meta.color : '#9ca3af' }}>{val}</div>
                <div style={styles.statLabel}>{meta.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ФИЛЬТРЫ */}
      <div style={styles.filters}>
        <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={styles.select}>
          <option value="">Все уровни</option>
          <option value="critical">🆘 Критично</option>
          <option value="error">🚨 Ошибки</option>
          <option value="warning">⚠️ Предупреждения</option>
          <option value="info">ℹ️ Инфо</option>
        </select>

        <select value={filterResolved} onChange={e => setFilterResolved(e.target.value as any)} style={styles.select}>
          <option value="false">Нерешённые</option>
          <option value="true">Решённые</option>
          <option value="">Все</option>
        </select>

        <input
          type="text"
          placeholder="Источник (hh-cron, auth, global-handler...)"
          value={filterSource}
          onChange={e => setFilterSource(e.target.value)}
          style={styles.input}
        />

        <input
          type="text"
          placeholder="Поиск по тексту"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          style={{ ...styles.input, flex: 1 }}
        />

        <button onClick={handleSearch} style={styles.searchBtn} disabled={loading}>🔍</button>
      </div>

      {/* LIST */}
      {error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {loading ? (
        <div style={styles.center}>Загрузка...</div>
      ) : items.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <p style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#16a34a' }}>
            {filterResolved === 'false' ? 'Ошибок нет!' : 'Пусто'}
          </p>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 14 }}>
            {filterResolved === 'false' ? 'Система работает штатно' : 'Попробуй другие фильтры'}
          </p>
        </div>
      ) : (
        <>
          <div style={styles.list}>
            {items.map(it => {
              const meta = LEVEL_META[it.level] || LEVEL_META.error;
              const isOpen = expanded.has(it._id);
              return (
                <div key={it._id} style={{ ...styles.item, borderLeft: `4px solid ${meta.color}`, opacity: it.resolved ? 0.6 : 1 }}>
                  <div style={styles.itemHeader} onClick={() => toggleExpand(it._id)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 22 }}>{meta.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ ...styles.levelBadge, background: meta.bg, color: meta.color }}>
                            {meta.label}
                          </span>
                          <span style={styles.sourceBadge}>{it.source}</span>
                          <span style={styles.date}>{fmtDate(it.createdAt)}</span>
                          {it.resolved && (
                            <span style={styles.resolvedBadge}>✅ Решено</span>
                          )}
                        </div>
                        <div style={styles.message}>{it.message}</div>
                      </div>
                    </div>
                    <div style={styles.itemActions}>
                      {!it.resolved && (
                        <button
                          onClick={e => { e.stopPropagation(); handleResolve(it._id); }}
                          style={styles.resolveBtn}
                          title="Пометить решённым"
                        >✅</button>
                      )}
                      {isSuperadmin && (
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(it._id); }}
                          style={styles.deleteBtn}
                          title="Удалить"
                        >🗑</button>
                      )}
                      <span style={styles.expandIcon}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={styles.details}>
                      {it.context && (
                        <div style={styles.detailBlock}>
                          <div style={styles.detailLabel}>Context:</div>
                          <pre style={styles.pre}>{JSON.stringify(it.context, null, 2)}</pre>
                        </div>
                      )}
                      {it.stack && (
                        <div style={styles.detailBlock}>
                          <div style={styles.detailLabel}>Stack trace:</div>
                          <pre style={styles.pre}>{it.stack}</pre>
                        </div>
                      )}
                      {it.resolved && (
                        <div style={styles.resolvedInfo}>
                          ✅ Решено {fmtDate(it.resolvedAt)}
                          {it.resolvedBy && ` пользователем ${it.resolvedBy.name || it.resolvedBy.email}`}
                          {it.resolvedNote && ` · ${it.resolvedNote}`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchItems(p); }}
                disabled={page <= 1 || loading}
                style={styles.pageBtn}
              >← Назад</button>
              <span style={{ fontSize: 14, color: '#666' }}>
                Стр. {page} из {totalPages} (всего: {total})
              </span>
              <button
                onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchItems(p); }}
                disabled={page >= totalPages || loading}
                style={styles.pageBtn}
              >Вперёд →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== Стили ====================
const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, flexWrap: 'wrap', gap: 8,
  },
  totalBadge: {
    background: '#dc2626', color: '#fff', borderRadius: 20,
    padding: '2px 12px', fontSize: 14, fontWeight: 700, minWidth: 28, textAlign: 'center',
  },
  refreshBtn: {
    padding: '8px 14px', borderRadius: 10, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 13,
  },
  resolveAllBtn: {
    padding: '8px 14px', borderRadius: 10, border: 'none',
    background: '#16a34a', color: '#fff', cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  },

  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  statCard: {
    padding: '14px', borderRadius: 12, border: '2px solid #e5e7eb',
    textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s',
  },
  statValue: { fontSize: 28, fontWeight: 700, marginTop: 4 },
  statLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },

  filters: {
    display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap',
  },
  select: {
    padding: '10px 14px', borderRadius: 10, border: '1px solid #ddd',
    fontSize: 13, background: '#fff', cursor: 'pointer',
  },
  input: {
    padding: '10px 14px', borderRadius: 10, border: '1px solid #ddd', fontSize: 13,
  },
  searchBtn: {
    padding: '10px 16px', borderRadius: 10, border: 'none',
    background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },

  errorBox: {
    padding: 14, background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16,
  },
  center: { textAlign: 'center', padding: 40, color: '#666' },
  empty: {
    textAlign: 'center', padding: '80px 20px', color: '#666',
    background: '#f9fafb', borderRadius: 14,
  },

  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  item: {
    background: '#fff', borderRadius: 10, padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  itemHeader: {
    display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
  },
  itemActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  levelBadge: {
    padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  sourceBadge: {
    padding: '2px 8px', borderRadius: 6, fontSize: 11,
    background: '#f3f4f6', color: '#4b5563', fontFamily: 'monospace',
  },
  date: { fontSize: 11, color: '#9ca3af' },
  resolvedBadge: {
    padding: '2px 8px', borderRadius: 6, fontSize: 11,
    background: '#dcfce7', color: '#166534', fontWeight: 600,
  },
  message: {
    marginTop: 6, fontSize: 14, color: '#111827', wordBreak: 'break-word',
  },
  resolveBtn: {
    border: 'none', background: '#dcfce7', color: '#166534',
    cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 6,
  },
  deleteBtn: {
    border: 'none', background: '#fee2e2', color: '#991b1b',
    cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 6,
  },
  expandIcon: { fontSize: 12, color: '#9ca3af', marginLeft: 4 },

  details: {
    marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6',
  },
  detailBlock: { marginBottom: 12 },
  detailLabel: {
    fontSize: 11, color: '#6b7280', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
  },
  pre: {
    background: '#1f2937', color: '#e5e7eb', padding: '10px 14px',
    borderRadius: 8, fontSize: 11, fontFamily: 'monospace',
    overflowX: 'auto', margin: 0, maxHeight: 300, overflowY: 'auto',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  resolvedInfo: {
    fontSize: 12, color: '#16a34a', background: '#f0fdf4',
    padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0',
  },

  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 12, marginTop: 20, flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '8px 14px', borderRadius: 10, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 13,
  },
};

export default SystemErrorsPanel;
